/**
 * Per-step countdown timers for cooking mode.
 *
 * The state machine is a pure reducer so it can be unit-tested and so a reload
 * can rehydrate it: elapsed time is stored as "already elapsed + the epoch this
 * run started", never as a decrementing counter, which means a suspended tab
 * (or a phone that locked for 40 minutes) catches up correctly on the next tick
 * rather than losing time.
 */

export type TimerStatus = 'idle' | 'running' | 'paused' | 'done';

export interface TimerState {
  stepId: string;
  durationMs: number;
  status: TimerStatus;
  /** Milliseconds counted before the current run segment started. */
  elapsedMs: number;
  /** Epoch ms this run segment began; null unless running. */
  startedAt: number | null;
  /** True once the alarm for a finished timer has been played. */
  acknowledged: boolean;
}

export type TimersState = Record<string, TimerState>;

export type TimerAction =
  | { type: 'start'; stepId: string; durationMs: number; now: number }
  | { type: 'pause'; stepId: string; now: number }
  | { type: 'resume'; stepId: string; now: number }
  | { type: 'reset'; stepId: string }
  | { type: 'tick'; now: number }
  | { type: 'acknowledge'; stepId: string }
  | { type: 'hydrate'; state: TimersState };

export const emptyTimers: TimersState = {};

/** Milliseconds counted so far, including the run in progress. */
export function elapsedAt(timer: TimerState, now: number): number {
  const running =
    timer.status === 'running' && timer.startedAt !== null ? now - timer.startedAt : 0;
  return Math.max(0, timer.elapsedMs + running);
}

/** Milliseconds left, floored at zero. */
export function remainingAt(timer: TimerState, now: number): number {
  return Math.max(0, timer.durationMs - elapsedAt(timer, now));
}

function complete(timer: TimerState): TimerState {
  return {
    ...timer,
    status: 'done',
    elapsedMs: timer.durationMs,
    startedAt: null,
    acknowledged: false,
  };
}

export function timersReducer(state: TimersState, action: TimerAction): TimersState {
  switch (action.type) {
    case 'hydrate':
      return action.state;

    case 'start': {
      // Starting always restarts from the full duration.
      const timer: TimerState = {
        stepId: action.stepId,
        durationMs: Math.max(0, action.durationMs),
        status: 'running',
        elapsedMs: 0,
        startedAt: action.now,
        acknowledged: false,
      };
      return { ...state, [action.stepId]: timer.durationMs === 0 ? complete(timer) : timer };
    }

    case 'pause': {
      const timer = state[action.stepId];
      if (!timer || timer.status !== 'running') return state;
      return {
        ...state,
        [action.stepId]: {
          ...timer,
          status: 'paused',
          elapsedMs: elapsedAt(timer, action.now),
          startedAt: null,
        },
      };
    }

    case 'resume': {
      const timer = state[action.stepId];
      if (!timer || timer.status !== 'paused') return state;
      return {
        ...state,
        [action.stepId]: { ...timer, status: 'running', startedAt: action.now },
      };
    }

    case 'reset': {
      const timer = state[action.stepId];
      if (!timer) return state;
      return {
        ...state,
        [action.stepId]: {
          ...timer,
          status: 'idle',
          elapsedMs: 0,
          startedAt: null,
          acknowledged: true,
        },
      };
    }

    case 'tick': {
      let changed = false;
      const next: TimersState = { ...state };
      for (const [stepId, timer] of Object.entries(state)) {
        if (timer.status === 'running' && remainingAt(timer, action.now) === 0) {
          next[stepId] = complete(timer);
          changed = true;
        }
      }
      return changed ? next : state;
    }

    case 'acknowledge': {
      const timer = state[action.stepId];
      if (!timer || timer.acknowledged) return state;
      return { ...state, [action.stepId]: { ...timer, acknowledged: true } };
    }

    default:
      return state;
  }
}

/** Timers that have finished and whose alarm has not been played yet. */
export function unacknowledgedTimers(state: TimersState): TimerState[] {
  return Object.values(state).filter((t) => t.status === 'done' && !t.acknowledged);
}

export function anyRunning(state: TimersState): boolean {
  return Object.values(state).some((t) => t.status === 'running');
}

/** "12:05" / "1:02:30" — a countdown people can read across the kitchen. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(h > 0 ? 2 : 1, '0');
  return h > 0 ? `${h}:${mm}:${String(s).padStart(2, '0')}` : `${mm}:${String(s).padStart(2, '0')}`;
}

// ------------------------------------------------------------ persistence --

const STORAGE_PREFIX = 'fewerbags:timers:';

export function timersStorageKey(planId: string): string {
  return `${STORAGE_PREFIX}${planId}`;
}

/** Restore timers for a plan; a reload mid-cook keeps every countdown running. */
export function loadTimers(planId: string): TimersState {
  if (typeof sessionStorage === 'undefined') return emptyTimers;
  try {
    const raw = sessionStorage.getItem(timersStorageKey(planId));
    if (!raw) return emptyTimers;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return emptyTimers;
    const out: TimersState = {};
    for (const [stepId, value] of Object.entries(parsed as Record<string, unknown>)) {
      const t = value as Partial<TimerState>;
      if (typeof t.durationMs !== 'number' || typeof t.status !== 'string') continue;
      out[stepId] = {
        stepId,
        durationMs: t.durationMs,
        status: t.status as TimerStatus,
        elapsedMs: typeof t.elapsedMs === 'number' ? t.elapsedMs : 0,
        startedAt: typeof t.startedAt === 'number' ? t.startedAt : null,
        acknowledged: t.acknowledged !== false,
      };
    }
    return out;
  } catch {
    return emptyTimers;
  }
}

export function saveTimers(planId: string, state: TimersState): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(timersStorageKey(planId), JSON.stringify(state));
  } catch {
    // Private mode / quota — timers simply do not survive a reload.
  }
}
